<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import checkedCheckbox from '@/assets/images/icons8-checked-checkbox.svg?component';
import uncheckedCheckbox from '@/assets/images/icons8-unchecked-checkbox.svg?component';
import xCheckbox from '@/assets/images/icons8-close-window.svg?component';

import type { Nullable } from '@immersive-tech/viewer-engine';

interface IProps {
  modelValue?: Nullable<boolean>;
  label?: string;
}

interface IEmits {
  (e: 'update:modelValue', value: Nullable<boolean>): unknown;
}

const emit = defineEmits<IEmits>();
const props = withDefaults(defineProps<IProps>(), {
  modelValue: null,
  label: () => '',
});
const input = ref<Nullable<HTMLInputElement>>(null);
const state = ref(-1);

const activeCheckboxImage = computed(() => {
  if (state.value === 0) return xCheckbox;
  if (state.value === 1) return uncheckedCheckbox;

  return checkedCheckbox;
});

const inputWatcher = (newValue: Nullable<HTMLInputElement>) => {
  if (!newValue) return;

  if (props.modelValue === false) {
    state.value = 0;
  } else if (props.modelValue === null) {
    state.value = 1;
  } else {
    state.value = 2;
  }
};

const stateWatcher = (newValue: number) => {
  if (!input.value) return;

  if (newValue === 0) {
    input.value.indeterminate = false;
    input.value.checked = false;
  } else if (newValue === 1) {
    input.value.indeterminate = true;
    input.value.checked = false;
  } else if (newValue === 2) {
    input.value.indeterminate = false;
    input.value.checked = true;
  }
};

const toggleState = () => {
  if (!input.value) return;

  state.value = (state.value + 1) % 3;

  if (state.value === 0) {
    emit('update:modelValue', false);
  } else if (state.value === 1) {
    emit('update:modelValue', null);
  } else if (state.value === 2) {
    emit('update:modelValue', true);
  }
};

watch(input, inputWatcher);
watch(state, stateWatcher);
</script>

<template>
  <label class="flex flex-row items-center justify-center cursor-pointer" @click="toggleState">
    <input ref="input" type="checkbox" class="hidden">
    <component :is="activeCheckboxImage" class="mr-[5px] w-[16px] min-w-[16px]" />
    <p v-if="props.label" class="text-xs break-word">{{ props.label }}</p>
  </label>
</template>
